import React, { useContext } from "react";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Menu,
  MenuItem,
  Avatar,
  Box,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { UserDetailsContext } from "../Context/userDetailsContext";
import { getAvatarInitials, getAvatarColor } from "../utils/avatarUtils";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import HomeIcon from "@mui/icons-material/Home";
import axios from "../axiosConfig";
import { resolveMediaUrl } from "../utils/mediaUrl";

const UserAvatarMenu = ({ anchorEl, open, onClose }) => {
  const { userDetails, setUserDetails } = useContext(UserDetailsContext);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fullName = `${userDetails?.citizen?.firstName} ${userDetails?.citizen?.familyName}`;
  const avatarColor = getAvatarColor(fullName);
  const avatarSrc = userDetails?.citizen?.profilePicturePreview
    || resolveMediaUrl(userDetails?.citizen?.profilePicture, { attachmentRoot: true });

  const handleProfileClick = () => {
    onClose();
    navigate("/my-account");
  };

  const handleLogout = async () => {
    try {
      await axios.post("/Auth/logout");
      
      // Clear all user data from context
      setUserDetails({});
      
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear service worker cache to prevent corrupted cache issues
      if ('serviceWorker' in navigator && 'caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
          console.log('Service worker cache cleared');
        } catch (cacheError) {
          console.error('Error clearing cache:', cacheError);
        }
      }
      
      onClose();
      
      // Use replace to prevent back button issues
      window.location.replace("/login");
    } catch (error) {
      console.error("Logout error:", error);
      
      // Still clear user details and redirect even if API fails
      setUserDetails({});
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear cache on error too
      if ('serviceWorker' in navigator && 'caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            caches.delete(cacheName);
          });
        }).catch(err => console.error('Cache clear error:', err));
      }
      
      onClose();
      window.location.replace("/login");
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      disablePortal={isMobile} // render inside DOM flow on small screens so it scrolls with page
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 300,
          maxWidth: 400,
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          direction: "rtl",
          overflow: "visible",
          // On mobile make the menu taller but scrollable
          maxHeight: isMobile ? '75vh' : 'auto',
          overflowY: isMobile ? 'auto' : 'visible',
          width: isMobile ? 'calc(100% - 32px)' : undefined,
          // make sure the paper is placed relatively when not portal'd
          position: isMobile ? 'absolute' : undefined,
          left: isMobile ? 16 : undefined,
          right: isMobile ? 16 : undefined,
          "&::before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            left: 14,
            width: 12,
            height: 12,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: "left", vertical: "top" }}
      anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
    >
      {/* User Header with avatar beside name */}
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", lineHeight: 1.3, mb: 0.5 }}>
              {userDetails?.citizen?.firstName} {userDetails?.citizen?.familyName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userDetails?.citizen?.documentType?.textA}
            </Typography>
          </Box>
          <Avatar
            src={avatarSrc}
            sx={{
              width: 70,
              height: 70,
              bgcolor: avatarSrc ? "transparent" : avatarColor,
              color: "white",
              fontSize: "1.8rem",
              fontWeight: "bold",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {!avatarSrc && getAvatarInitials(fullName)}
          </Avatar>
        </Box>
      </Box>

      <Divider />

      {/* User Information */}
      <Box sx={{ p: 2, bgcolor: "grey.50" }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <BadgeIcon sx={{ color: "primary.main", fontSize: 20 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                رقم الوثيقة
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "500" }}>
                {userDetails?.citizen?.documentNumber}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <EmailIcon sx={{ color: "primary.main", fontSize: 20 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                البريد الإلكتروني
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "500",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {userDetails?.citizen?.email}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <PhoneIcon sx={{ color: "primary.main", fontSize: 20 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                رقم الهاتف
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "500" }}>
                {userDetails?.citizen?.phone1}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <HomeIcon sx={{ color: "primary.main", fontSize: 20 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
              المدينة
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "500" }}>
                {userDetails?.citizen?.country?.name} - {userDetails?.citizen?.city}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Box>

      <Divider />

      {/* Menu Items */}
      <MenuItem
        onClick={handleProfileClick}
        sx={{
          py: 1.5,
          px: 2.5,
          "&:hover": {
            backgroundColor: "primary.light",
                          // opacity: 0.1,

            "& .MuiTypography-root": {
            },
          },
        }}
      >
        <ListItemIcon>
          <PersonIcon color="primary" />
        </ListItemIcon>
        <ListItemText>
          <Typography variant="body2" sx={{ fontWeight: "500" }}>
            الملف الشخصي
          </Typography>
        </ListItemText>
      </MenuItem>

      <Divider />

      <MenuItem
        onClick={handleLogout}
        sx={{
          py: 1.5,
          px: 2.5,
          "&:hover": {
            backgroundColor: "error.lighter",
          },
        }}
      >
        <ListItemIcon>
          <LogoutIcon color="error" />
        </ListItemIcon>
        <ListItemText>
          <Typography variant="body2" color="error" sx={{ fontWeight: "500" }}>
            تسجيل الخروج
          </Typography>
        </ListItemText>
      </MenuItem>
    </Menu>
  );
};

export default UserAvatarMenu;
